import React, { useEffect, useState } from 'react';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import '../../styles/swagger-overrides.css';
import yaml from 'js-yaml';
import { api } from '../../lib/api';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import type { Application } from '../../types';
import { createSchemaTablePlugin } from './SchemaTablePlugin';
import type { ExternalAuthConfig, ExternalCredentials } from '../../types/auth-config';
import { Lock, Zap, Eye, EyeOff, Check, Edit2, Copy, Search } from 'lucide-react';
import { AppCredentialsManager } from '../AppCredentialsManager';
import { useAuthStore } from '../../stores/authStore';
import { toast } from '../../utils/toast';

interface Endpoint {
  id: string;
  name: string;
  description: string;
  public_method: string;
  public_path: string;
  validation_rules: any;
  rate_limit_per_minute: number | null;
  rate_limit_per_day: number | null;
}

interface ProductEndpointsResponse {
  data: {
    product_id: string;
    product_name: string;
    authentication_provider: string;
    endpoints: Endpoint[];
  };
}

interface ApiDocumentationProps {
  productId: string;
  openApiSpec: string | null;
  applications?: Application[];
  apiKey?: string;
  subscriptionId?: string;
  authProvider?: string;
  productName?: string;
}

// localStorage keys — persist credentials across page navigation and reloads
const getStorageKey = (productId: string) => `swagger_auth_${productId}`;

interface PersistedAuth {
  apiKey?: string;
  basicAuth?: { username: string; password: string };
  savedAt: number;
}

function loadPersistedAuth(productId: string): PersistedAuth | null {
  try {
    const raw = localStorage.getItem(getStorageKey(productId));
    if (!raw) return null;
    const parsed: PersistedAuth = JSON.parse(raw);
    // Expire after 8 hours
    if (Date.now() - parsed.savedAt > 8 * 60 * 60 * 1000) {
      localStorage.removeItem(getStorageKey(productId));
      return null;
    }
    console.log(
      '[SwaggerAuth] Loaded credentials for product',
      productId,
      '| keys:',
      Object.keys(parsed).filter((k) => k !== 'savedAt')
    );
    return parsed;
  } catch {
    return null;
  }
}

function savePersistedAuth(productId: string, auth: Omit<PersistedAuth, 'savedAt'>) {
  try {
    const data = { ...auth, savedAt: Date.now() };
    localStorage.setItem(getStorageKey(productId), JSON.stringify(data));
    console.log(
      '[SwaggerAuth] Saved credentials for product',
      productId,
      '| keys:',
      Object.keys(auth)
    );
  } catch {
    /* non-fatal */
  }
}

export const ApiDocumentation: React.FC<ApiDocumentationProps> = ({
  productId,
  openApiSpec,
  applications,
  apiKey,
  authProvider: propAuthProvider,
  productName,
}) => {
  const { environment } = useEnvironment();
  const { client } = useAuthStore();
  const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
  const [authProvider, setAuthProvider] = useState<string>(propAuthProvider || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [externalAuthConfig] = useState<ExternalAuthConfig | null>(null);

  // Active application (first in list)
  const activeApp = applications && applications.length > 0 ? applications[0] : null;

  // Custom API Key state for Swagger UI testing
  const storageKey = `swagger_apikey_${productId}_${activeApp?.id || 'default'}`;
  const [customApiKey, setCustomApiKey] = useState<string>(() => {
    return (
      localStorage.getItem(storageKey) ||
      localStorage.getItem(`swagger_apikey_${productId}`) ||
      apiKey ||
      ''
    );
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [isEditingKey, setIsEditingKey] = useState(!customApiKey);
  const [keySavedToast, setKeySavedToast] = useState(false);
  const [loadingAppKey, setLoadingAppKey] = useState(false);
  const [copiedEndpointId, setCopiedEndpointId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [methodFilter, setMethodFilter] = useState<string>('ALL');

  const filteredEndpoints = React.useMemo(() => {
    return endpoints.filter((endpoint) => {
      const matchesMethod =
        methodFilter === 'ALL' ||
        endpoint.public_method.toUpperCase() === methodFilter;
      const q = searchQuery.toLowerCase().trim();
      const matchesQuery =
        !q ||
        endpoint.public_path.toLowerCase().includes(q) ||
        endpoint.name.toLowerCase().includes(q) ||
        (endpoint.description && endpoint.description.toLowerCase().includes(q));
      return matchesMethod && matchesQuery;
    });
  }, [endpoints, searchQuery, methodFilter]);

  const handleCopyCurl = (endpoint: Endpoint) => {
    const isQas =
      typeof window !== 'undefined' &&
      window.location.hostname.includes('tatusolutions.com');
    const gatewayBase = isQas
      ? 'https://api-gateway.tatusolutions.com'
      : window.location.origin;

    const normalizedPath = endpoint.public_path.startsWith('/')
      ? endpoint.public_path
      : `/${endpoint.public_path}`;
    const url = `${gatewayBase}${normalizedPath}`;
    const key =
      customApiKey ||
      apiKey ||
      (activeApp as any)?.api_key ||
      activeApp?.api_key_prefix ||
      'YOUR_API_KEY';

    const curlCmd = `curl -X ${endpoint.public_method.toUpperCase()} "${url}" \\\n  -H "X-API-Key: ${key}" \\\n  -H "Accept: application/json"`;

    navigator.clipboard.writeText(curlCmd);
    setCopiedEndpointId(endpoint.id);
    toast.success('Comando cURL copiado para a área de transferência!');
    setTimeout(() => setCopiedEndpointId(null), 2000);
  };

  // Auto-load the application's Kong API Key from backend when available
  const fetchApplicationApiKey = React.useCallback(
    async (silent = true) => {
      if (!client?.id || !activeApp?.id) return;
      try {
        setLoadingAppKey(true);
        const res = await api.get<{
          success: boolean;
          data: { api_key?: string | null; api_key_prefix?: string };
        }>(`/clients/${client.id}/applications/${activeApp.id}/api-key`);
        const fetchedKey = res.data?.data?.api_key;
        if (fetchedKey) {
          setCustomApiKey(fetchedKey);
          localStorage.setItem(storageKey, fetchedKey);
          setIsEditingKey(false);
          if (!silent) {
            setKeySavedToast(true);
            setTimeout(() => setKeySavedToast(false), 2500);
          }
        }
      } catch {
        // Non-fatal fallback if key must be pasted manually
      } finally {
        setLoadingAppKey(false);
      }
    },
    [client?.id, activeApp?.id, storageKey]
  );

  useEffect(() => {
    const saved =
      localStorage.getItem(storageKey) ||
      localStorage.getItem(`swagger_apikey_${productId}`) ||
      apiKey ||
      '';
    if (saved) {
      setCustomApiKey(saved);
      setIsEditingKey(false);
    } else if (client?.id && activeApp?.id) {
      fetchApplicationApiKey(true);
    }
  }, [storageKey, productId, apiKey, client?.id, activeApp?.id, fetchApplicationApiKey]);

  const handleSaveApiKey = () => {
    if (customApiKey.trim()) {
      localStorage.setItem(storageKey, customApiKey.trim());
      setIsEditingKey(false);
      setKeySavedToast(true);
      setTimeout(() => setKeySavedToast(false), 2500);
    } else {
      localStorage.removeItem(storageKey);
      setIsEditingKey(true);
    }
  };

  // Load persisted auth from localStorage on mount
  const [persistedAuth] = useState<PersistedAuth | null>(() => loadPersistedAuth(productId));

  // Credentials loaded from persisted auth or empty
  const [externalCredentials] = useState<ExternalCredentials>(() => {
    const auth = loadPersistedAuth(productId);
    if (auth?.basicAuth) {
      return {
        username: auth.basicAuth.username,
        password: auth.basicAuth.password,
      } as ExternalCredentials;
    }
    return {} as ExternalCredentials;
  });

  // The effective API key to use (custom saved, passed prop, or fallback prefix)
  const effectiveApiKey =
    customApiKey.trim() || persistedAuth?.apiKey || apiKey || activeApp?.api_key_prefix || '';
  const resolvedAuthProvider = propAuthProvider || authProvider || 'BASIC_AUTH';

  // Parse OpenAPI spec and inject environment-aware servers
  const parsedOpenApiSpec = React.useMemo(() => {
    if (!openApiSpec) return null;

    try {
      // Parse spec (JSON or YAML)
      let spec: any;
      const raw = openApiSpec;
      if (typeof raw === 'object') {
        spec = JSON.parse(JSON.stringify(raw));
      } else if (raw.trim().startsWith('{')) {
        spec = JSON.parse(raw);
      } else {
        spec = yaml.load(raw) as any;
      }

      if (!spec) return null;

      // Build server list — point to the Kong Gateway base URL derived from VITE_API_BASE_URL
      const rawEnvBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) || '';
      let kongBase = 'http://localhost:8000';
      if (rawEnvBase && /^https?:\/\//i.test(rawEnvBase)) {
        try {
          const parsedUrl = new URL(rawEnvBase);
          kongBase = parsedUrl.origin;
        } catch {
          kongBase = rawEnvBase.replace(/\/portal\/?$/, '').replace(/\/+$/, '');
        }
      } else if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
        kongBase = window.location.origin.replace('ts-gateway.', 'api-gateway.');
      }
      const isSandbox = environment === 'sandbox';

      // Extract the product base path from the existing servers in the spec.
      // e.g. "http://localhost:8000/sandbox/rupe" → "/rupe"
      // e.g. "http://localhost:8000/v1/rupe"     → "/rupe"
      let productBasePath = '';
      if (spec.servers && Array.isArray(spec.servers) && spec.servers.length > 0) {
        const firstUrl: string = spec.servers[0].url || '';
        // Remove the kong prefix (/sandbox or /v1) to get the product path
        const match = firstUrl.match(/\/(?:sandbox|v1)(\/.*)?$/);
        if (match && match[1]) {
          productBasePath = match[1]; // e.g. "/rupe"
        }
      }

      spec.servers = [
        {
          url: `${kongBase}/${isSandbox ? 'sandbox' : 'v1'}${productBasePath}`,
          description: isSandbox
            ? 'Sandbox — Kong Gateway (ativo)'
            : 'Produção — Kong Gateway (ativo)',
        },
        {
          url: `${kongBase}${productBasePath}`,
          description: 'Rota Direta — Kong Gateway',
        },
        {
          url: `${kongBase}/${isSandbox ? 'v1' : 'sandbox'}${productBasePath}`,
          description: isSandbox ? 'Produção — Kong Gateway' : 'Sandbox — Kong Gateway',
        },
      ];

      return spec;
    } catch (err) {
      console.error('Failed to parse OpenAPI spec:', err);
      return null;
    }
  }, [openApiSpec, environment]);

  // Build external authentication headers
  const buildExternalAuthHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {};

    if (!externalAuthConfig || externalAuthConfig.type === 'none') {
      return headers;
    }

    // Adicionar header X-Product-ID para o plugin Kong identificar o produto
    headers['X-Product-ID'] = productId;

    // Construir headers baseado no tipo de autenticação
    switch (externalAuthConfig.type) {
      case 'basic_auth': {
        const username = externalCredentials['username'] || externalCredentials['user'];
        const password = externalCredentials['password'] || externalCredentials['pass'];
        if (username && password) {
          headers['X-External-Username'] = username;
          headers['X-External-Password'] = password;
        }
        break;
      }

      case 'bearer_token': {
        const token = externalCredentials['token'] || externalCredentials['access_token'];
        if (token) {
          headers['X-External-Token'] = token;
        }
        break;
      }

      case 'api_key': {
        const apiKey = externalCredentials['api_key'] || externalCredentials['key'];
        if (apiKey) {
          headers['X-External-API-Key'] = apiKey;
        }
        break;
      }

      case 'oauth2': {
        const clientId = externalCredentials['client_id'];
        const clientSecret = externalCredentials['client_secret'];
        if (clientId && clientSecret) {
          headers['X-External-Client-ID'] = clientId;
          headers['X-External-Client-Secret'] = clientSecret;
        }
        break;
      }

      case 'custom_headers': {
        // Para custom headers, adicionar cada campo como X-External-{field}
        Object.entries(externalCredentials).forEach(([key, value]) => {
          if (value) {
            // Capitalizar primeira letra de cada palavra
            const headerName = `X-External-${key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, '-')}`;
            headers[headerName] = value;
          }
        });
        break;
      }
    }

    return headers;
  };

  // Fetch endpoints from database if no OpenAPI spec
  useEffect(() => {
    if (!parsedOpenApiSpec && productId) {
      setLoading(true);
      setError(null);

      api
        .get<ProductEndpointsResponse>(`/products/${productId}/endpoints`)
        .then((response) => {
          setEndpoints(response.data.data.endpoints);
          setAuthProvider(response.data.data.authentication_provider);
        })
        .catch((err) => {
          console.error('Failed to fetch endpoints:', err);
          setError('Failed to load API endpoints');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [productId, parsedOpenApiSpec]);

  // Render OpenAPI spec using Swagger UI
  if (parsedOpenApiSpec) {
    return (
      <div>
        {/* Authentication Requirements */}
        <div className="mb-6 p-6 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="text-slate-900 dark:text-white font-bold text-lg flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary-600" /> Autenticação & Credenciais de Teste
            </h3>
            <span className="badge-modern badge-primary text-xs">
              Ambiente {environment === 'sandbox' ? 'Sandbox' : 'Produção'}
            </span>
          </div>

          {/* API Gateway Authentication */}
          <div className="bg-white dark:bg-slate-800/60 p-4 rounded-xl border border-slate-200 dark:border-slate-700/60 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase font-bold text-slate-500 tracking-wider">
                1. Autenticação Kong Gateway (X-API-Key)
              </span>
              {keySavedToast && (
                <span className="text-xs font-semibold text-success-600 dark:text-success-400 flex items-center gap-1">
                  <Check className="w-3.5 h-3.5" /> Chave guardada!
                </span>
              )}
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
              O Gateway valida esta chave para autorizar as requisições (
              {activeApp ? `Aplicação: ${activeApp.name}` : 'Nenhuma aplicação associada'}).
            </p>

            {isEditingKey ? (
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={customApiKey}
                  onChange={(e) => setCustomApiKey(e.target.value)}
                  placeholder={
                    activeApp
                      ? `${activeApp.api_key_prefix || (activeApp.sandbox_mode ? 'mk_test_' : 'mk_live_')}... (Cole a API Key completa)`
                      : 'Cole a API Key completa (mk_test_... ou mk_live_...)'
                  }
                  className="flex-1 min-w-[240px] px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-sm font-mono text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                  title={showApiKey ? 'Ocultar' : 'Mostrar'}
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                {activeApp && client?.id && (
                  <button
                    type="button"
                    disabled={loadingAppKey}
                    onClick={() => fetchApplicationApiKey(false)}
                    className="btn-base btn-secondary text-xs px-3 py-2 font-semibold"
                    title="Carregar automaticamente a API Key desta aplicação"
                  >
                    {loadingAppKey ? 'A carregar...' : 'Usar Chave da Aplicação'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSaveApiKey}
                  className="btn-base btn-primary text-xs px-4 py-2 font-semibold"
                >
                  Guardar
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900 p-2.5 rounded-lg border border-slate-200 dark:border-slate-700">
                <code className="text-xs font-mono text-slate-800 dark:text-slate-200">
                  {showApiKey
                    ? customApiKey
                    : `${(customApiKey || activeApp?.api_key_prefix || 'mk_test_').substring(0, 10)}************************`}
                </code>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                  >
                    {showApiKey ? 'Ocultar' : 'Mostrar'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsEditingKey(true)}
                    className="text-xs text-primary-600 dark:text-primary-400 font-semibold hover:underline flex items-center gap-1"
                  >
                    <Edit2 className="w-3 h-3" /> Alterar
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* External API Authentication Manager */}
          {activeApp && client?.id && (
            <div className="bg-white dark:bg-slate-800/60 p-4 rounded-xl border border-slate-200 dark:border-slate-700/60">
              <span className="text-xs uppercase font-bold text-slate-500 tracking-wider block mb-1">
                2. Credenciais Externas de Destino (Injetadas pelo Gateway)
              </span>
              <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">
                As credenciais da API do fornecedor externo (ex: MinFin SIGFE / RUPE) associadas a
                esta aplicação:
              </p>
              <AppCredentialsManager
                applicationId={activeApp.id}
                clientId={client.id}
                productId={productId}
                authProvider={resolvedAuthProvider}
                productName={productName}
              />
            </div>
          )}
        </div>

        {/* Swagger UI Container */}
        <div className="swagger-container bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4 overflow-hidden">
          <SwaggerUI
            spec={parsedOpenApiSpec}
            docExpansion="list"
            filter={true}
            defaultModelsExpandDepth={2}
            defaultModelExpandDepth={2}
            defaultModelRendering="example"
            displayRequestDuration={true}
            tryItOutEnabled={true}
            persistAuthorization={true}
            showExtensions={false}
            showCommonExtensions={false}
            requestInterceptor={(req) => {
              // Add API Gateway authentication
              const keyToUse = effectiveApiKey;
              if (keyToUse) {
                req.headers['X-API-Key'] = keyToUse;
                req.headers['apikey'] = keyToUse;
              }

              // Add external API authentication headers (from DynamicAuthFields if configured)
              const externalAuthHeaders = buildExternalAuthHeaders();
              Object.entries(externalAuthHeaders).forEach(([key, value]) => {
                req.headers[key] = value;
              });

              return req;
            }}
            onComplete={(swaggerUi: any) => {
              // Pre-authorize with persisted credentials so the user doesn't need to re-enter them
              if (persistedAuth?.apiKey) {
                swaggerUi.preauthorizeApiKey('ApiKeyAuth', persistedAuth.apiKey);
              }
              if (persistedAuth?.basicAuth) {
                swaggerUi.preauthorizeBasicAuth(
                  'BasicAuth',
                  persistedAuth.basicAuth.username,
                  persistedAuth.basicAuth.password
                );
              }
            }}
            requestSnippetsEnabled={false}
            plugins={[
              createSchemaTablePlugin(parsedOpenApiSpec),
              // Plugin to capture auth changes and persist to localStorage
              () => ({
                statePlugins: {
                  auth: {
                    wrapActions: {
                      authorize: (oriAction: any) => (payload: any) => {
                        // Debug: log the payload structure to understand the format
                        console.log(
                          '[SwaggerAuth] authorize payload:',
                          JSON.stringify(payload, null, 2)
                        );
                        // Persist when user clicks "Authorize"
                        const update: Omit<PersistedAuth, 'savedAt'> = {};
                        if (payload?.ApiKeyAuth?.value) {
                          update.apiKey = payload.ApiKeyAuth.value;
                        }
                        if (payload?.BasicAuth?.username) {
                          update.basicAuth = {
                            username: payload.BasicAuth.username,
                            password: payload.BasicAuth.password || '',
                          };
                        }
                        if (Object.keys(update).length > 0) {
                          savePersistedAuth(productId, update);
                        }
                        return oriAction(payload);
                      },
                      logout: (oriAction: any) => (payload: any) => {
                        // Clear persisted auth when user logs out
                        const current = loadPersistedAuth(productId);
                        if (current) {
                          const keys = Object.keys(payload || {});
                          if (keys.includes('ApiKeyAuth')) delete current.apiKey;
                          if (keys.includes('BasicAuth')) delete current.basicAuth;
                          if (current.apiKey || current.basicAuth) {
                            savePersistedAuth(productId, current);
                          } else {
                            localStorage.removeItem(getStorageKey(productId));
                          }
                        }
                        return oriAction(payload);
                      },
                    },
                  },
                },
              }),
            ]}
          />
        </div>
      </div>
    );
  }

  // Generate basic documentation from product_endpoints table
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <p className="mt-3 text-gray-600" style={{ fontSize: 'var(--text-sm)' }}>
            Carregando documentação da API...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-error-50 border border-error-200 rounded-xl">
        <p
          className="text-error-700"
          style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-medium)',
          }}
        >
          {error}
        </p>
      </div>
    );
  }

  if (endpoints.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 mb-4">
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mx-auto"
          >
            <path
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <p
          className="text-gray-600 mb-2"
          style={{
            fontSize: 'var(--text-base)',
            fontWeight: 'var(--font-medium)',
          }}
        >
          Nenhum endpoint disponível
        </p>
        <p className="text-gray-500" style={{ fontSize: 'var(--text-sm)' }}>
          Esta API ainda não possui endpoints documentados.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Authentication Requirements */}
      <div className="mb-6 p-6 bg-primary-50 border border-primary-200 rounded-xl">
        <h3
          className="text-primary-900 mb-3 flex items-center gap-2"
          style={{
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-semibold)',
          }}
        >
          <span>
            <Lock className="w-5 h-5" />
          </span>{' '}
          Autenticação Necessária
        </h3>
        <p className="text-primary-800 mb-2" style={{ fontSize: 'var(--text-sm)' }}>
          Esta API usa autenticação <strong>{authProvider || 'API Key'}</strong>.
        </p>
        {applications && applications.length > 0 && (
          <div className="flex items-center gap-2 mt-2">
            <span
              className="text-primary-800"
              style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
              }}
            >
              Sua API Key:
            </span>
            <code
              className="bg-primary-100 text-primary-900 px-3 py-1 rounded-lg"
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {applications[0].api_key_prefix}
            </code>
          </div>
        )}
      </div>

      {/* Endpoints Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Pesquisar por endpoint, caminho ou descrição..."
            className="w-full pl-9 pr-4 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all"
          />
        </div>

        <div className="flex items-center gap-1.5 p-1 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-semibold overflow-x-auto">
          {(['ALL', 'GET', 'POST', 'PUT', 'DELETE'] as const).map((method) => (
            <button
              key={method}
              type="button"
              onClick={() => setMethodFilter(method)}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all ${
                methodFilter === method
                  ? 'bg-white dark:bg-slate-700 text-primary-600 dark:text-primary-400 shadow-2xs'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              {method === 'ALL' ? 'Todos' : method}
            </button>
          ))}
        </div>
      </div>

      <div className="text-xs text-slate-500 dark:text-slate-400 mb-3 font-medium">
        A mostrar {filteredEndpoints.length} de {endpoints.length} endpoints documentados
      </div>

      {/* Endpoints List */}
      <div className="space-y-4">
        {filteredEndpoints.length === 0 ? (
          <div className="text-center py-8 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-dashed border-slate-200 dark:border-slate-700 p-6">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Nenhum endpoint encontrado para o filtro "{searchQuery || methodFilter}".
            </p>
          </div>
        ) : (
          filteredEndpoints.map((endpoint) => (
          <div key={endpoint.id} className="card-modern p-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span
                  className={`badge-modern ${
                    endpoint.public_method === 'GET'
                      ? 'badge-primary'
                      : endpoint.public_method === 'POST'
                        ? 'badge-success'
                        : endpoint.public_method === 'PUT'
                          ? 'badge-warning'
                          : endpoint.public_method === 'DELETE'
                            ? 'badge-error'
                            : 'badge-secondary'
                  }`}
                >
                  {endpoint.public_method}
                </span>
                <code
                  className="text-gray-900 bg-gray-100 px-3 py-1 rounded-lg"
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                  }}
                >
                  {endpoint.public_path}
                </code>
              </div>

              <button
                type="button"
                onClick={() => handleCopyCurl(endpoint)}
                className="btn-base btn-secondary btn-xs inline-flex items-center gap-1.5 text-xs shrink-0 self-start sm:self-auto hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors shadow-2xs"
                title="Copiar comando cURL com API Key pronta a executar no terminal"
              >
                {copiedEndpointId === endpoint.id ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                    <span className="text-emerald-700 font-semibold">Copiado!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5 text-slate-500" />
                    <span>Copiar cURL</span>
                  </>
                )}
              </button>
            </div>

            <h3
              className="text-gray-900 mb-2"
              style={{
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-semibold)',
              }}
            >
              {endpoint.name}
            </h3>

            {endpoint.description && (
              <p className="text-gray-600 mb-3" style={{ fontSize: 'var(--text-sm)' }}>
                {endpoint.description}
              </p>
            )}

            {/* Rate Limits */}
            {(endpoint.rate_limit_per_minute || endpoint.rate_limit_per_day) && (
              <div className="mb-3 p-3 bg-warning-50 border border-warning-200 rounded-lg">
                <h4
                  className="text-warning-900 mb-1 flex items-center gap-2"
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-semibold)',
                  }}
                >
                  <span>
                    <Zap className="w-4 h-4" />
                  </span>{' '}
                  Limites de Taxa
                </h4>
                <div className="text-warning-800" style={{ fontSize: 'var(--text-sm)' }}>
                  {endpoint.rate_limit_per_minute && (
                    <p>• {endpoint.rate_limit_per_minute} requisições por minuto</p>
                  )}
                  {endpoint.rate_limit_per_day && (
                    <p>• {endpoint.rate_limit_per_day} requisições por dia</p>
                  )}
                </div>
              </div>
            )}

            {/* Validation Rules */}
            {endpoint.validation_rules && Object.keys(endpoint.validation_rules).length > 0 && (
              <div>
                <h4
                  className="text-gray-700 mb-2"
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-semibold)',
                  }}
                >
                  Parâmetros
                </h4>
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <pre
                    className="text-xs overflow-x-auto"
                    style={{ fontFamily: 'var(--font-mono)' }}
                  >
                    {JSON.stringify(endpoint.validation_rules, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        ))
      )}
      </div>
    </div>
  );
};
